import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, nextTick, provide, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router';
import Dropzone from './Home/dropzone.vue';
import Status from './Home/status.vue';
import Invoice_list from './Home/invoice_list.vue';
import Invoice_information_edit from './Home/invoice_information_edit.vue';
import Pdf_viewer from './Home/pdf_viewer.vue';
import JSZip from 'jszip';
import { store as data_store } from '../store.ts'
import File from '../module/common/models/File.ts';
import Invoice from '../module/common/models/Invoice.ts';
import Uuid from '../module/common/models/Uuid.ts';
import { parse, merge_invoice_pdfs } from '../module/pdf/module.ts'
import { Size } from '../components/basic_button/size.ts'
import Error_zone from '../module/error_handler/components/error_zone.vue';
import { ErrorZone } from '../module/error_handler/types/enums.ts';
import Error from '../module/error_handler/types/Error.ts';
import Confirm_button from '../components/confirm_button.vue';
import { load_pdf_fonts } from '../module/pdf/pdf_writer.ts';
import { save_invoice, search_invoice } from '../module/invoices/module.ts';
import Invoice_comparison from './Home/invoice_comparison.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'Home',
  setup(__props, { expose: __expose }) {
  __expose();

const is_stamping = ref(false);

const store = data_store()
const router = useRouter()

provide('store', store)

const files = computed(() => store.files)
const selected_file_id = computed(() => store.selected_file_id)
const selected_invoice_ids = computed(() => store.selected_invoice_ids)
const file = computed(() => {
    let loc_selected_file_id: Uuid|null = selected_file_id.value
    const loc_files = files.value

    if (loc_selected_file_id === null) {
        return null
    } else {
        let loc_file = loc_files.find((tmp_file) => {
            return tmp_file.id.get() == loc_selected_file_id.get()
        })

        if (!loc_file) {
            return null
        } else {
            return loc_file
        }
    }
})


const show_invoice_comparison = ref(false)

const selected_invoices = computed(() => {
    return store.selected_invoices
})

const invoices_to_save = computed(() => {
    const loc_files = files
    let rtn_invoices_to_save: Invoice[] = []

    for (const tmp_file of loc_files.value) {
        const tmp_invoices_to_add = tmp_file.invoices.filter((tmp_invoice) => {
            return !tmp_invoice.excluded
        })
        rtn_invoices_to_save = rtn_invoices_to_save.concat(tmp_invoices_to_add)
    }

    return rtn_invoices_to_save
})

const duplicated_invoices = computed(() => {
    const loc_invoices = invoices_to_save.value

    return loc_invoices.filter((tmp_invoice) => {
        return tmp_invoice.duplicated
    })
})

// Methods
function select_additional_invoices(file: File, invoices: Invoice[]) {
    store.select_additional_invoices(file, invoices)
}

function clear_and_select_invoices(file: File, invoices: Invoice[]) {
    store.clear_and_select_invoices(file, invoices)
    if (invoices.length === 1) {
        const invoice = invoices[0]
        if (invoice.invoice_information.vendor === "") {
            nextTick().then(() => {
                document.getElementById('vendor')?.focus()
            })
        } else {
            nextTick().then(() => {
                document.getElementById('project_NaN')?.focus()
            })
        }
    }
}

function merge_invoices(file: File, invoice_ids_to_merge: Uuid[]) {
    store.merge_invoices(file, invoice_ids_to_merge)

    if (invoice_ids_to_merge.length > 0) {
        const loc_invoice = file.invoices.find((tmp_invoice) => {
            tmp_invoice.id.get() === invoice_ids_to_merge[0].get()
        })
        if (typeof loc_invoice !== 'undefined') {
            store.clear_and_select_invoices(file, [loc_invoice])
        }
    }
}

function exclude_invoice(file: File, invoice: Invoice, excluded: boolean) {
    const updated_invoice = {
        ...invoice,
        excluded: excluded
    }

    update_invoice(file, updated_invoice);
    store.check_for_duplicates()
}

function stamp_pdf(file: File, invoice: Invoice, stamped_pdf_bytes: Uint8Array<ArrayBufferLike>, stamp_x:number, stamp_y: number) {
    is_stamping.value = false;

    const updated_invoice = {
        ...invoice,
        stamped: true,
        excluded: false,
        stamp_x: stamp_x,
        stamp_y: stamp_y,
        stamped_pdf_bytes: stamped_pdf_bytes
    };

    update_invoice(file, updated_invoice);
}
function remove_pdf_page(arg_file: File, arg_invoice: Invoice, arg_pdf_bytes: Uint8Array<ArrayBufferLike>, arg_stamped_pdf_bytes: Uint8Array<ArrayBufferLike>|null) {
    const loc_updated_invoice = {
        ...arg_invoice,
        pdf_bytes: arg_pdf_bytes,
        stamped_pdf_bytes: arg_stamped_pdf_bytes
    };

    update_invoice(arg_file, loc_updated_invoice);
}


function file_accepted(file_id, file_name, file_type, file_size, file_last_modified) {
    store.add_file(file_id, file_name, file_type, file_size, file_last_modified)
}

function update_invoice(file: File, invoice: Invoice) {
    file.update_invoice(invoice)

    store.update_file(file)
}

async function update_invoice_and_stamp(file: File, invoice: Invoice) {
    await store.update_invoice_and_stamp(file, invoice)
    store.check_for_duplicates()
}

async function file_loaded(arg_file_id: Uuid, arg_file_byte_array: Uint8Array<ArrayBufferLike>) {
    const loc_file: File | undefined = files.value.find((tmp_file) => tmp_file.id.get() == arg_file_id.get())
    if (typeof loc_file == "undefined") {
        return
    }

    let loc_parsed_invoices: Invoice[] 

    try {
        loc_parsed_invoices = await parse(arg_file_byte_array)

        const loc_invoices_with_project_numbers = loc_parsed_invoices.filter((tmp_invoice: Invoice) => {
            return tmp_invoice.invoice_information.project_NaN != ''
        })

        loc_file.invoices = loc_parsed_invoices
        store.update_file(loc_file)

        store.check_for_duplicates()

        for (let tmp_invoice of loc_invoices_with_project_numbers) {
            store.fetch_project_data(loc_file.id, tmp_invoice.id, tmp_invoice.invoice_information.project_NaN)
        }
    } catch (original_error) {
        if ("id" in original_error
            && "error_message" in original_error
            && "error_class" in original_error
        ) {
            const error: Error = original_error
            error.error_zone = ErrorZone.dropzone

            if (error.error_message == "File was not a valid PDF") {
                error.error_message = loc_file.file_name + ": was not a valid PDF"
            }

            store.log_error(error)
        } else {
            throw original_error
        }

        store.delete_file(loc_file)
    } finally {
        loc_file.loading = false
    }
}

function delete_file(file: File) {
    store.delete_file(file)
    store.check_for_duplicates()
}

async function print_all(invoices: Invoice[]) {
    const merged_pdf_bytes = await merge_invoice_pdfs(invoices, true)

    const blob = new Blob([merged_pdf_bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url);

    for (const tmp_invoice of invoices) {
        save_invoice(tmp_invoice)
    }

    if (printWindow) {
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };
    } else {
        console.error('Failed to open the print window.');
    }
}

async function clear_all() {
    store.clear_all()
    store.check_for_duplicates()
}

async function download_all(invoices: Invoice[]) {
    const zip = new JSZip();

    function display_currency(money: number | string | null) {
        function is_numeric(str: number | string): boolean {
            if (typeof str == "number") return true
            if (typeof str != "string") return false // we only process strings!  
            return !isNaN(str as unknown as number) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
                !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
        }

        if(typeof money == "undefined"
            || money === null
            || !is_numeric(money)
        ) {
            return ""
        }

        if (typeof money === "string") {
            money = parseFloat(money)
        }

        return money.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }

    for (const tmp_invoice of invoices) {
        let tmp_filename = ""
        if (tmp_invoice.invoice_information.vendor === '' 
            || tmp_invoice.invoice_information.invoice_NaN === ''
        ) {
            tmp_filename = tmp_invoice.id.get() + " - Unrecongnized invoice.pdf"
        } else {
            tmp_filename = tmp_invoice.invoice_information.vendor + " - " + tmp_invoice.invoice_information.invoice_NaN + " " + display_currency(tmp_invoice.invoice_information.total) + ".pdf"
        }

        zip.file(tmp_filename, tmp_invoice.stamped_pdf_bytes);
        save_invoice(tmp_invoice)
    }

    const zip_blob = await zip.generateAsync({ type: 'blob' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(zip_blob);
    link.download = 'invoices.zip';
    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
}

function test_invoice_pull() {
    search_invoice()
}
// function start_auth() {
    
//     function getRandomInt(max) {
//         return Math.floor(Math.random() * max);
//     }

//     let challenge = new Uint8Array()
//     for (let i = 0; i < 255; i++) {
//         challenge[i] = getRandomInt(254)
//     }

//     const createCredentialOptions = {
//         publicKey: {
//             challenge: challenge,
//             rp: {
//                 name: "Shaw Electric - Invoices",
//                 id: "localhost",
//             },
//             user: {
//                 id: new Uint8Array(16),
//                 name: "david@vranish.io",
//                 displayName: "David Vranish",
//             },
//             pubKeyCredParams: [
//             {
//                 type: "public-key",
//                 alg: -7,
//             },
//             ],
//         },
//     };

//     navigator.credentials
//         .create(createCredentialOptions)
//         .then((newCredentialInfo) => {
//             const response = newCredentialInfo.response;
//             const clientExtensionsResults =
//             newCredentialInfo.getClientExtensionResults();
//             console.log('done', response, clientExtensionsResults)

//         })
//         .catch((err) => {
//             console.error(err);
//         });
// }

// async function login_auth() {
    
//     function getRandomInt(max) {
//         return Math.floor(Math.random() * max);
//     }

//     let challenge = new Uint8Array()
//     for (let i = 0; i < 255; i++) {
//         challenge[i] = getRandomInt(254)
//     }
    
//     // Call WebAuthn authentication
//     const publicKeyCredentialRequestOptions = {
//         // Server generated challenge
//         challenge: challenge,
//         // The same RP ID as used during registration
//         rpId: "localhost",
//         hints: "client-device"
//     };

//     const abortController = new AbortController();
//     try {
//         const credential = await navigator.credentials.get({
//             publicKey: publicKeyCredentialRequestOptions,
//             // signal: abortController.signal,
//             // Specify 'conditional' to activate conditional UI
//             // mediation: "conditional",
//         });
//     } catch (e) {
//         console.log('e', e)    
//     }
    

//     console.log('credential', credential)
// }

onMounted(() => {
    load_pdf_fonts()
})


const __returned__ = { is_stamping, store, router, files, selected_file_id, selected_invoice_ids, file, show_invoice_comparison, selected_invoices, invoices_to_save, duplicated_invoices, select_additional_invoices, clear_and_select_invoices, merge_invoices, exclude_invoice, stamp_pdf, remove_pdf_page, file_accepted, update_invoice, update_invoice_and_stamp, file_loaded, delete_file, print_all, clear_all, download_all, test_invoice_pull, Dropzone, Status, Invoice_list, Invoice_information_edit, Pdf_viewer, get Size() { return Size }, Error_zone, get ErrorZone() { return ErrorZone }, Confirm_button, Invoice_comparison }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})